import {inject, Injectable} from '@angular/core';
import {
  GetSelectDataParams,
  RecordData,
  RecordsDataRequestQueryParams,
  RecordUpdateRequestParams,
  SelectData
} from "../../../../types/records-table.types";
import {
  bankDetailsResourceType,
  dateFields,
  propertyLazySelectBankDetails
} from "../configs/bank-details-modal-config";
import {FormGroup} from "@angular/forms";
import {ValidationService} from "../../../../services/validation.service";
import {ErrorService} from "../../../../services/error.service";
import {AttachProps, DetachProps} from "../../../../types/attach-detach.types";
import {RecordTableApiService} from "../../../../services-api/record-table-api.service";
import {
  BankDetailsDataInTable,
  CreateBankDetailsParams
} from "../types/bank-details-modal.types";
import {BankDetailsModalApiService} from "./bank-details-modal-api.service";
import {defaultPageableRecordData} from "../../../../models/default-pageable-record-data";
import {Pageable} from "../../../../types/pagable.types";
import {BankDetailsRequestQueryParamsStateService} from "./bank-details-request-query-params-state.service";
import {GetRecordPropertyDataByIdParams} from "../../base-modal/types/base-modal.types";
import {BaseModalApiService} from "../../services/base-modal-api.service";
import {BankDetailsFormField} from "../types/bank-details-form-field.types";
import {bankDetailsFormFields} from "../configs/bank-details-form-fields";
import {BaseModalService} from "../../services/base-modal.service";

@Injectable({
  providedIn: 'root'
})
export class BankDetailsModalService {
  private validationService: ValidationService = inject(ValidationService);
  private errorService: ErrorService = inject(ErrorService);
  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  private bankDetailsModalApiService: BankDetailsModalApiService = inject(BankDetailsModalApiService);
  private bankDetailsRequestQueryParamsStateService: BankDetailsRequestQueryParamsStateService = inject(BankDetailsRequestQueryParamsStateService);
  private baseModalApiService: BaseModalApiService = inject(BaseModalApiService);
  private baseModalService: BaseModalService = inject(BaseModalService);

  public createAttachProps(recordCalendarId: number, bankDetailsId: number): AttachProps {
    return {
      recordCalendarId: recordCalendarId,
      id: bankDetailsId,
      path: bankDetailsResourceType.path
    };
  }

  public isBirthDateValid(formGroup: FormGroup): boolean {
    const isBirthValid = this.validationService.compareDates({
      earlierDate: formGroup.get('birthDate')?.value,
      laterDate: formGroup.get('grandDate')?.value,
      checkFutureDates: true
    });

    if (isBirthValid) { return true; }

    this.errorService.showErrorMessage('Дата рождения не может быть позже даты выдачи, и обе даты не могут превышать текущую!');
    return false;
  }

  public getSelectData(id: number): Promise<Array<SelectData>> {
    const getSelectDataParams: GetSelectDataParams = {
      propertyLazySelect: propertyLazySelectBankDetails,
      id: id
    };

    return new Promise((resolve, reject) => {
      this.recordTableApiService.fetchSelectData(getSelectDataParams).subscribe({
        next: (selectData: Array<SelectData>) => resolve(selectData),
        error: (error) => reject(error)
      });
    })
  }

  public updateBankDetailsToTable(bankDetailsId: number | null, recordData: RecordData): BankDetailsDataInTable {
    const bloggerId = (recordData['bankDetails'] as RecordData)['bloggerId'];
    return {
      id: bankDetailsId,
      valid: !!bankDetailsId,
      bloggerId: Number(bloggerId)
    };
  }

  public createBankDetails(createBankDetailsParams: CreateBankDetailsParams): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.bankDetailsModalApiService.createBankDetails(createBankDetailsParams).subscribe({
        next: (bankDetailsData: RecordData) => resolve(bankDetailsData),
        error: (error) => reject(error)
      });
    });
  }

  public setFormGroups(data: Array<RecordData>): Array<FormGroup> {
    return data.map((bankDetails) => {
      bankDetails = this.baseModalService.convertDates<RecordData>(bankDetails, dateFields);
      const formGroup = this.baseModalService.getFormGroup<BankDetailsFormField>(bankDetailsFormFields);
      formGroup.patchValue(bankDetails);
      return formGroup;
    });
  }

  public async fetchBankDetailsData(bloggerId: number | null): Promise<Pageable<RecordData>> {
    this.bankDetailsRequestQueryParamsStateService.updateRecordsDataRequestQueryParams(bloggerId);
    return await this.getRecordData(bankDetailsResourceType.path, this.bankDetailsRequestQueryParamsStateService.bankDetailsRequestQueryParamsState())
      .catch(() => defaultPageableRecordData);
  }

  public updatedRecordField<T>(recordUpdateRequestParams: RecordUpdateRequestParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.updateRecord<T>(recordUpdateRequestParams).subscribe({
        next: (recordData: T) => resolve(recordData),
        error: (error) => reject(error)
      });
    });
  }

  public deleteRecord(path: string, recordId: string | number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.deleteRecord(path, recordId).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public fetchDataById<T>(params: GetRecordPropertyDataByIdParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.fetchDataById<T>(params).subscribe({
        next: (recordPropertyData: T) => resolve(recordPropertyData),
        error: (error) => reject(error)
      });
    });
  }

  public attachRecordData(attachProps: AttachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.attachRecordData(attachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public detachRecordData(detachProps: DetachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.detachRecordData(detachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  private getRecordData(path: string, queryParams: RecordsDataRequestQueryParams): Promise<Pageable<RecordData>> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.fetchRecordTableData(path, queryParams).subscribe({
        next: (recordsTableData: Pageable<RecordData>) => resolve(recordsTableData),
        error: (error) => reject(error)
      })
    });
  }
}
